import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CheckCircle, Calendar, MapPin, Monitor, Users, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c6a1162-6fec-4154-8fe3-0b918a141fc6/artifacts/ojlspj1s_Untitled%20design.png';

export default function TNAFormPage() {
  const { enrollmentId } = useParams();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState(null);
  
  const [form, setForm] = useState({
    overall_objective: '',
    topics_to_emphasize: '',
    topics_to_reduce: '',
    participant_level: 'intermediate',
    preferred_language: 'english',
    materials_in_arabic: 'no',
    preferred_case_studies: '',
    additional_notes: ''
  });

  useEffect(() => {
    fetchEnrollment();
  }, [enrollmentId]);

  const fetchEnrollment = async () => {
    try {
      const response = await fetch(`${API}/tna/${enrollmentId}`);
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        if (data.tna_completed) {
          setSubmitted(true);
        }
      } else {
        setError('Enrollment not found or link is invalid.');
      }
    } catch (err) {
      setError('Failed to load enrollment details.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.overall_objective.trim()) {
      toast.error(language === 'ar' ? 'يرجى ذكر الهدف العام' : 'Please provide your overall objective');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        enrollment_id: enrollmentId,
        course_title: enrollment.course_title,
        ...form
      };
      
      const response = await fetch(`${API}/tna/${enrollmentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success(language === 'ar' ? 'تم إرسال التقييم بنجاح' : 'TNA form submitted successfully');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit form');
      }
    } catch (err) {
      toast.error('Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-semibold text-[#111827] mb-2">Link Invalid</h2>
            <p className="text-[#6B7280] mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#111827] mb-2">
              {language === 'ar' ? 'تم الإرسال بنجاح' : 'Thank You!'}
            </h2>
            <p className="text-[#6B7280] mb-4">
              {language === 'ar' 
                ? 'تم استلام تقييم احتياجاتك التدريبية. سيستخدم فريقنا هذه المعلومات لتخصيص الدورة وفقًا لاحتياجاتك.'
                : 'Your Training Needs Assessment has been received. Our team will use this information to tailor the course to your specific needs.'}
            </p>
            <div className="bg-[#F0F9FF] rounded-lg p-4 text-left">
              <p className="font-medium text-[#111827]">{enrollment.course_title}</p>
              <p className="text-sm text-[#6B7280]">
                {enrollment.city} | {enrollment.start_date} - {enrollment.end_date}
              </p>
            </div>
            <Link to="/" className="inline-block mt-6">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="WeDo HR" className="h-10" />
          </Link>
          <span className="text-sm text-[#6B7280]">Training Needs Assessment</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Course Context Card */}
        <Card className="mb-6 bg-gradient-to-r from-[#33AADD] to-[#2899cc] text-white">
          <CardContent className="p-6">
            <h1 className="text-2xl font-bold mb-4">
              {language === 'ar' ? enrollment.course_title_ar || enrollment.course_title : enrollment.course_title}
            </h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white/90">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{language === 'ar' ? enrollment.city_ar || enrollment.city : enrollment.city}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{enrollment.start_date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Monitor className="h-4 w-4" />
                <span className="capitalize">{enrollment.delivery_mode}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>{enrollment.full_name}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* TNA Form */}
        <Card>
          <CardHeader>
            <CardTitle>
              {language === 'ar' ? 'تقييم الاحتياجات التدريبية' : 'Training Needs Assessment'}
            </CardTitle>
            <p className="text-[#6B7280] text-sm">
              {language === 'ar' 
                ? 'يرجى ملء هذا النموذج لمساعدتنا في تخصيص الدورة وفقًا لاحتياجاتك'
                : 'Please complete this form to help us tailor the course to your specific needs'}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Q1: Overall Objective */}
              <div className="space-y-2">
                <Label htmlFor="overall_objective" className="text-base font-medium">
                  1. {language === 'ar' ? 'ما هو الهدف العام لهذه الدورة لك / لفريقك؟' : 'What is the overall objective of this course for you / your team?'} *
                </Label>
                <Textarea
                  id="overall_objective"
                  value={form.overall_objective}
                  onChange={(e) => handleChange('overall_objective', e.target.value)}
                  rows={3}
                  placeholder={language === 'ar' ? 'مثال: تطوير مهارات القيادة الاستراتيجية للمديرين التنفيذيين...' : 'e.g., Develop strategic leadership skills for our executive team...'}
                  required
                  data-testid="tna-objective"
                />
              </div>

              {/* Q2: Topics to Emphasize */}
              <div className="space-y-2">
                <Label htmlFor="topics_to_emphasize" className="text-base font-medium">
                  2. {language === 'ar' ? 'ما المواضيع التي تريد منا التركيز عليها أكثر؟' : 'Which topics would you like us to emphasize more?'}
                </Label>
                <Textarea
                  id="topics_to_emphasize"
                  value={form.topics_to_emphasize}
                  onChange={(e) => handleChange('topics_to_emphasize', e.target.value)}
                  rows={2}
                  placeholder={language === 'ar' ? 'اختياري - اذكر المواضيع المحددة...' : 'Optional - List specific topics...'}
                  data-testid="tna-emphasize"
                />
              </div>

              {/* Q3: Topics to Reduce */}
              <div className="space-y-2">
                <Label htmlFor="topics_to_reduce" className="text-base font-medium">
                  3. {language === 'ar' ? 'هل هناك مواضيع تريد منا تقليلها أو تخطيها؟' : 'Are there any topics you want us to reduce, skip, or treat briefly?'}
                </Label>
                <Textarea
                  id="topics_to_reduce"
                  value={form.topics_to_reduce}
                  onChange={(e) => handleChange('topics_to_reduce', e.target.value)}
                  rows={2}
                  placeholder={language === 'ar' ? 'اختياري...' : 'Optional...'}
                  data-testid="tna-reduce"
                />
              </div>

              {/* Q4: Participant Level */}
              <div className="space-y-3">
                <Label className="text-base font-medium">
                  4. {language === 'ar' ? 'ما هو المستوى الحالي للمشاركين في هذا الموضوع؟' : 'What is the current level of the participants regarding this topic?'}
                </Label>
                <RadioGroup 
                  value={form.participant_level} 
                  onValueChange={(v) => handleChange('participant_level', v)}
                  className="flex flex-col sm:flex-row gap-4"
                  data-testid="tna-level"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="beginner" id="level-beginner" />
                    <Label htmlFor="level-beginner" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'مبتدئ' : 'Beginner'}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="intermediate" id="level-intermediate" />
                    <Label htmlFor="level-intermediate" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'متوسط' : 'Intermediate'}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="advanced" id="level-advanced" />
                    <Label htmlFor="level-advanced" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'متقدم' : 'Advanced'}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Q5: Preferred Language */}
              <div className="space-y-3">
                <Label className="text-base font-medium">
                  5. {language === 'ar' ? 'لغة التدريب المفضلة' : 'Preferred delivery language'}
                </Label>
                <RadioGroup 
                  value={form.preferred_language} 
                  onValueChange={(v) => handleChange('preferred_language', v)}
                  className="flex flex-col sm:flex-row gap-4"
                  data-testid="tna-language"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="english" id="lang-english" />
                    <Label htmlFor="lang-english" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'الإنجليزية' : 'English'}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="arabic" id="lang-arabic" />
                    <Label htmlFor="lang-arabic" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'العربية' : 'Arabic'}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bilingual" id="lang-bilingual" />
                    <Label htmlFor="lang-bilingual" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'ثنائي اللغة' : 'Bilingual'}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Q6: Materials in Arabic */}
              <div className="space-y-3">
                <Label className="text-base font-medium">
                  6. {language === 'ar' ? 'هل تحتاج المواد التدريبية بالعربية؟' : 'Do you need the training materials in Arabic?'}
                </Label>
                <RadioGroup 
                  value={form.materials_in_arabic} 
                  onValueChange={(v) => handleChange('materials_in_arabic', v)}
                  className="flex flex-col sm:flex-row gap-4"
                  data-testid="tna-materials"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="mat-yes" />
                    <Label htmlFor="mat-yes" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'نعم' : 'Yes'}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="mat-no" />
                    <Label htmlFor="mat-no" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'لا' : 'No'}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="both" id="mat-both" />
                    <Label htmlFor="mat-both" className="font-normal cursor-pointer">
                      {language === 'ar' ? 'العربية والإنجليزية' : 'Both Arabic and English'}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Q7: Preferred Case Studies */}
              <div className="space-y-2">
                <Label htmlFor="preferred_case_studies" className="text-base font-medium">
                  7. {language === 'ar' ? 'ما نوع الأمثلة أو دراسات الحالة الأكثر فائدة لك؟' : 'What type of examples or case studies would be most useful for you?'}
                </Label>
                <Textarea
                  id="preferred_case_studies"
                  value={form.preferred_case_studies}
                  onChange={(e) => handleChange('preferred_case_studies', e.target.value)}
                  rows={2}
                  placeholder={language === 'ar' ? 'مثال: حالات من قطاع النفط والغاز، أمثلة إقليمية...' : 'e.g., Oil & gas sector cases, regional examples...'}
                  data-testid="tna-cases"
                />
              </div>

              {/* Q8: Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="additional_notes" className="text-base font-medium">
                  8. {language === 'ar' ? 'أي ملاحظات أو توقعات أو تحديات عمل يجب مراعاتها؟' : 'Any specific notes, expectations, or business challenges we should keep in mind?'}
                </Label>
                <Textarea
                  id="additional_notes"
                  value={form.additional_notes}
                  onChange={(e) => handleChange('additional_notes', e.target.value)}
                  rows={3}
                  placeholder={language === 'ar' ? 'اختياري...' : 'Optional...'}
                  data-testid="tna-notes"
                />
              </div>

              {/* Submit */}
              <div className="pt-4 border-t">
                <Button 
                  type="submit" 
                  className="w-full sm:w-auto btn-primary" 
                  disabled={submitting}
                  data-testid="tna-submit"
                >
                  {submitting 
                    ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') 
                    : (language === 'ar' ? 'إرسال التقييم' : 'Submit Assessment')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
